// Imports
import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Link } from "react-router-dom";

// UI Imports
import Grid from "@material-ui/core/Grid/Grid";
import TextField from "@material-ui/core/TextField";
import Toolbar from "@material-ui/core/Toolbar";
import Button from "@mui/material/Button";
import Typography from "@material-ui/core/Typography";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import Tooltip from '@material-ui/core/Tooltip';
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import IconButton from "@material-ui/core/IconButton";
import UploadIcon from "@mui/icons-material/Upload";
import IconArrowBack from "@material-ui/icons/ArrowBack";
import Switch from "@mui/material/Switch";
import { withStyles } from "@material-ui/core/styles";
import styles from "./styles";
import RichTextEditor, { stripImageHostFromContent, restoreImageHostInContent } from "../../common/HtmlEditor";
import { checkPermission } from "../../common/Sidebar/checkPermission";

// App Imports
import {
  nullToEmptyString,
  capitalizeFirstLetter,
  subString,
} from "../../../setup/helpers";
import { upload, messageShow } from "../../common/api/actions";
import SectionPaper from "../../common/SectionPaper";
import BrandedLoading from "../../common/BrandedLoading";
import ImageComp from "../../common/Image";
import { list, detail, getCategories } from "../api/actions/query";
import { createOrUpdate } from "../api/actions/mutation";
import routes, { getImageSource } from "../api/routes";

// Component
class MediaGalleryCreateOrUpdate extends Component {
  constructor(props) {
    super(props);

    this.mediaGallery = {
      _id: "",
      title: "",
      subtitle: "",
      description: "",
      mediaType: "image",
      mediaUrl: "default.jpg",
      videoUrl: "",
      thumbnailUrl: "",
      ctaText: "",
      ctaLink: "",
      sort: 0,
      isActive: true,
      mediaGalleryCategoryId: "",
      rating: 5,
      tags: "",
    };

    this.state = {
      isLoading: false,
      isLoadingSubmit: false,
      isUploadingFile: false,
      isUploadingThumbnail: false,
      mediaGallery: this.mediaGallery,
      previewImage: null,
      previewThumbnail: null,
      categories: [],
      permissions: {
        canView: false,
        canCreate: false,
        canEdit: false,
        canDelete: false,
      }
    };
  }

  componentDidMount() {
    const {
      match: {
        params: { mediaGalleryId },
      },
      location
    } = this.props;

    this.verifyPermissions();
    this.loadCategories();

    // Get categoryId from URL search params for new media creation
    if (!mediaGalleryId && location.search) {
      const urlParams = new URLSearchParams(location.search);
      const categoryId = urlParams.get('categoryId');

      if (categoryId) {
        this.setState(prevState => ({
          mediaGallery: {
            ...prevState.mediaGallery,
            mediaGalleryCategoryId: categoryId
          }
        }));
      }
    }

    // Get media gallery details if edit
    if (mediaGalleryId) {
      this.getMediaGallery(mediaGalleryId);
    }
  }

  // Load categories
  loadCategories = async () => {
    try {
      const { data } = await getCategories();
      if (data.success) {
        this.setState({ categories: data.data || [] });
      }
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  }

  verifyPermissions = async () => {
    const modulePermissions = await checkPermission("marketing", "mediaGallery");
    this.setState({
      permissions: {
        canView: modulePermissions.canView,
        canCreate: modulePermissions.canCreate,
        canEdit: modulePermissions.canEdit,
        canDelete: modulePermissions.canDelete
      }
    });
  }

  getMediaGallery = async (mediaGalleryId) => {
    const { detail, messageShow } = this.props;

    this.isLoadingToggle(true);

    try {
      const { data } = await detail({ mediaGalleryId });

      if (data.success) {
        let mediaGallery = data.data;

        if (Array.isArray(mediaGallery.tags)) {
          mediaGallery.tags = mediaGallery.tags.join(", ");
        }

        // Restore absolute image URLs for CKEditor display
        if (mediaGallery.description) {
          mediaGallery.description = restoreImageHostInContent(mediaGallery.description);
        }

        this.setState({
          mediaGallery: mediaGallery,
        });
      }
    } catch (error) {
      messageShow("Some error occurred. Please try again.");
    } finally {
      this.isLoadingToggle(false);
    }
  };

  onCreateOrUpdate = async (event) => {
    event.preventDefault();

    const { createOrUpdate, messageShow, history } = this.props;
    const { mediaGallery } = this.state;

    if (mediaGallery.mediaType === "video" && !mediaGallery.videoUrl) {
      messageShow("Video URL is required for video type media.");
      return;
    }

    /*if (!mediaGallery.mediaUrl || mediaGallery.mediaUrl === "default.jpg") {
      messageShow("Please upload an image.");
      return;
    }*/
    if (mediaGallery.mediaType === "image" && (!mediaGallery.mediaUrl || mediaGallery.mediaUrl === "default.jpg")) {
      messageShow("Image URL is required for image type media.");
      return;
    }

    if (!mediaGallery.mediaGalleryCategoryId) {
      messageShow("Please select a category.");
      return;
    }

    if (mediaGallery.rating < 0 || mediaGallery.rating > 5) {
      messageShow("Rating must be between 0 and 5.");
      return;
    }

    this.isLoadingSubmitToggle(true);

    // Transform data types for API
    const params = {
      ...mediaGallery,
      description: stripImageHostFromContent(mediaGallery.description || ''),
      rating: mediaGallery.rating ? Number(mediaGallery.rating) : 0,
      tags: typeof mediaGallery.tags === 'string'
        ? mediaGallery.tags.split(',').map(tag => tag.trim()).filter(tag => tag !== "")
        : (Array.isArray(mediaGallery.tags) ? mediaGallery.tags : [])
    };

    try {
      const { data } = await createOrUpdate(params);

      this.isLoadingSubmitToggle(false);

      messageShow(data.message);

      if (data.success) {
        history.push(routes.mediaGalleryList.path);
      }
    } catch (error) {
      this.isLoadingSubmitToggle(false);
      messageShow("Some error occurred. Please try again.");
    }
  };

  isLoadingToggle = (isLoading) => {
    this.setState({
      isLoading,
    });
  };

  isLoadingSubmitToggle = (isLoadingSubmit) => {
    this.setState({
      isLoadingSubmit,
    });
  };

  isUploadingFileToggle = (isUploadingFile) => {
    this.setState({
      isUploadingFile,
    });
  };

  isUploadingThumbnailToggle = (isUploadingThumbnail) => {
    this.setState({
      isUploadingThumbnail,
    });
  };

  onUploadMedia = async (event) => {
    const { upload, messageShow } = this.props;
    const imgFile = event.target.files[0];

    if (imgFile.size > 1048576) {
      messageShow("Media file size should not exceed 1MB.");
      return;
    }

    // Check image dimensions
    const checkImageDimensions = (file) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const width = img.naturalWidth;
          const height = img.naturalHeight;

          // Check if dimensions exceed limits
          if (width > 1280 || height > 720) {
            reject({
              message: `Image dimensions should not exceed 1280px × 720px. Current dimensions: ${width}px × ${height}px`,
              width,
              height
            });
          } else {
            resolve({ width, height });
          }
        };
        img.onerror = () => {
          reject({ message: "Invalid image file" });
        };
        img.src = URL.createObjectURL(file);
      });
    };

    try {
      // Validate image dimensions before upload
      await checkImageDimensions(imgFile);

      messageShow("Uploading media file, please wait...");
      this.isUploadingFileToggle(true);

      let file = new FormData();
      file.append("type", "mediaGallery");
      file.append("name", this.state.mediaGallery.title || "media");
      file.append("oldUrl", this.state.mediaGallery.mediaUrl || "");
      file.append("file", imgFile);

      const fileReader = new FileReader();
      fileReader.onload = () => {
        const fileResult = fileReader.result;
        const dataAsImg = new Image();
        dataAsImg.src = fileResult;
        this.setState({ previewImage: dataAsImg.src });
      };
      fileReader.readAsDataURL(imgFile);

      const { data } = await upload(file);

      if (data.success) {
        messageShow("Media file uploaded successfully.");

        const { mediaGallery } = this.state;

        if (mediaGallery.mediaType === "image") {
          mediaGallery.mediaUrl = data.file;
        } else if (mediaGallery.mediaType === "video") {
          mediaGallery.mediaUrl = data.file;
        }

        this.setState({ mediaGallery });
      } else {
        messageShow("There was some error. Please try again.");
      }
    } catch (error) {
      // Handle both dimension validation and upload errors
      if (error.message) {
        messageShow(error.message);
      } else {
        messageShow("There was some error. Please try again.");
      }
    } finally {
      this.isUploadingFileToggle(false);
    }
  };

  onChange = ({ target: { name, value } }) => {
    let finalValue = value;

    if (name === "rating") {
      if (value !== "") {
        const num = parseFloat(value);
        if (num > 5) finalValue = 5;
      }
    }

    this.setState((prevState) => {
      const updatedMediaGallery = {
        ...prevState.mediaGallery,
        [name]: finalValue
      };

      if (name === "mediaType" && value === "image") {
        updatedMediaGallery.videoUrl = "";
        updatedMediaGallery.thumbnailUrl = "";
      }

      return {
        mediaGallery: updatedMediaGallery
      };
    });
  };

  render() {
    const {
      match: {
        params: { mediaGalleryId },
      },
      classes,
    } = this.props;
    const {
      mediaGallery,
      isLoading,
      isLoadingSubmit,
      isUploadingFile,
      isUploadingThumbnail,
      permissions,
      previewImage,
      previewThumbnail,
      categories
    } = this.state;

    return (
      <div>
        <Toolbar className={classes.toolbar}>
          <Link to={routes.mediaGalleryList.path}>
            <IconButton className={classes.menuButton} color="inherit">
              <IconArrowBack />
            </IconButton>
          </Link>

          <Typography variant="h6" color="inherit" className={classes.grow}>
            {mediaGalleryId ? "Edit" : "Create"} Media Gallery
          </Typography>
        </Toolbar>

        <Grid item xs={12} lg={8}>
          <SectionPaper padding>
            {isLoading ? (
              <BrandedLoading />
            ) : (
              <form onSubmit={this.onCreateOrUpdate}>
                {/* ✅ NEW: Category Selection */}
                {/* <Grid item xs={12} style={{ marginBottom: 14 }}>
                  <FormControl fullWidth margin="dense">
  <InputLabel id="category-select-label">Select Category</InputLabel>
  <Select
    labelId="category-select-label"
    id="category-select"
    name="mediaGalleryCategoryId"
    value={mediaGallery.mediaGalleryCategoryId || ''}
    onChange={this.onChange}
    required
  >
    {categories.map((category) => (
      <MenuItem key={category._id} value={category._id}>
        {category.name}
      </MenuItem>
    ))}
  </Select>
</FormControl>

                </Grid>*/}
                {mediaGallery.mediaGalleryCategoryId && (
                  <Grid item xs={12} style={{ marginBottom: 14 }}>
                    <Typography variant="body2" color="textSecondary">
                      Category: {categories.find(cat =>
                        cat._id === (
                          typeof mediaGallery.mediaGalleryCategoryId === 'string'
                            ? mediaGallery.mediaGalleryCategoryId
                            : mediaGallery.mediaGalleryCategoryId?._id
                        )
                      )?.name || 'Unknown'
                      }
                    </Typography>
                  </Grid>
                )}

                {/* Input - Title */}
                <Grid item xs={12}>
                  <TextField
                    name={"title"}
                    value={nullToEmptyString(mediaGallery.title)}
                    onChange={this.onChange}
                    label={"Title"}
                    placeholder={"Enter title"}
                    required={true}
                    margin={"dense"}
                    autoComplete={"off"}
                    fullWidth
                  />
                </Grid>

                {/* Input - Subtitle */}
                <Grid item xs={12}>
                  <TextField
                    name={"subtitle"}
                    value={nullToEmptyString(mediaGallery.subtitle)}
                    onChange={this.onChange}
                    label={"Subtitle"}
                    placeholder={"Enter subtitle"}
                    margin={"dense"}
                    autoComplete={"off"}
                    fullWidth
                  />
                </Grid>

                {/* Input - Rating (Success Stories) */}
                <Grid item xs={12}>
                  <TextField
                    name={"rating"}
                    value={mediaGallery.rating}
                    onChange={this.onChange}
                    label={"Rating (0-5)"}
                    placeholder={"Enter rating"}
                    type="number"
                    inputProps={{ min: 0, max: 5, step: 1 }}
                    margin={"dense"}
                    autoComplete={"off"}
                    fullWidth
                    helperText="Used for Star Rating in Success Stories (Allowed: 0-5)"
                  />
                </Grid>
                {/* Input - Description (CKEditor) */}
                <Grid item xs={12} style={{ marginTop: 20 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Description
                  </Typography>
                  <RichTextEditor
                    value={this.state.mediaGallery.description || ""}
                    onChange={(data) =>
                      this.setState((prevState) => ({
                        mediaGallery: {
                          ...prevState.mediaGallery,
                          description: data,
                        },
                      }))
                    }
                    uploadType="mediaGallery"
                  />
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                    Main content or story details displayed in the modal
                  </Typography>
                </Grid>

                {/* Input - Tags */}
                <Grid item xs={12} style={{ marginTop: 20 }}>
                  <TextField
                    name={"tags"}
                    value={nullToEmptyString(mediaGallery.tags)}
                    onChange={this.onChange}
                    label={"Tags"}
                    placeholder={"Enter tags separated by comma"}
                    margin={"dense"}
                    autoComplete={"off"}
                    fullWidth
                    helperText="Badges displayed at the bottom of Success Story cards"
                  />
                </Grid>

                {/* Media Type */}
                <Grid item xs={12} style={{ marginTop: 14 }}>
                  <FormControl component="fieldset" required>
                    <FormLabel component="legend">Media Type</FormLabel>
                    <RadioGroup
                      aria-label="mediaType"
                      name="mediaType"
                      value={mediaGallery.mediaType}
                      onChange={this.onChange}
                      required
                      row
                    >
                      <FormControlLabel
                        value="image"
                        control={<Radio color="primary" />}
                        label="Image"
                        labelPlacement="end"
                      />
                      <FormControlLabel
                        value="video"
                        control={<Radio color="primary" />}
                        label="Video"
                        labelPlacement="end"
                      />
                      <FormControlLabel
                        value="text"
                        control={<Radio color="primary" />}
                        label="Text Only"
                        labelPlacement="end"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {/* CTA Text */}
                <Grid item xs={12}>
                  <TextField
                    name={"ctaText"}
                    value={nullToEmptyString(mediaGallery.ctaText)}
                    onChange={this.onChange}
                    label={"CTA Text"}
                    placeholder={"Enter call-to-action text"}
                    margin={"dense"}
                    autoComplete={"off"}
                    fullWidth
                  />
                </Grid>

                {/* CTA Link */}
                <Grid item xs={12}>
                  <TextField
                    name={"ctaLink"}
                    value={nullToEmptyString(mediaGallery.ctaLink)}
                    onChange={this.onChange}
                    label={"CTA Link"}
                    placeholder={"Enter call-to-action link"}
                    margin={"dense"}
                    autoComplete={"off"}
                    fullWidth
                  />
                </Grid>
                {/* Sort Order Field */}
                <Grid item xs={12}>
                  <TextField
                    name={"sort"}
                    value={mediaGallery.sort}
                    onChange={this.onChange}
                    label={"Sort Order"}
                    placeholder={"Enter sort order (0 = default, higher numbers = higher priority)"}
                    type="number"
                    margin={"dense"}
                    autoComplete={"off"}
                    fullWidth
                    helperText="Lower numbers appear first. Use 0 for default order."
                  />
                </Grid>
                {/* Video-specific fields - Only shown for video type */}
                {mediaGallery.mediaType === "video" && (
                  <>
                    {/* Video URL Text Field */}
                    <Grid item xs={12} style={{ marginTop: 20 }}>
                      <TextField
                        name={"videoUrl"}
                        value={nullToEmptyString(mediaGallery.videoUrl)}
                        onChange={this.onChange}
                        label={"Video URL"}
                        placeholder={"Enter video URL (e.g., YouTube, Vimeo)"}
                        required={true}
                        margin={"dense"}
                        autoComplete={"off"}
                        fullWidth
                      />
                    </Grid>

                    {/* Thumbnail URL Text Field */}
                    <Grid item xs={12}>
                      <TextField
                        name={"thumbnailUrl"}
                        value={nullToEmptyString(mediaGallery.thumbnailUrl)}
                        onChange={this.onChange}
                        label={"Thumbnail Image URL"}
                        placeholder={"Enter thumbnail image URL (optional)"}
                        margin={"dense"}
                        autoComplete={"off"}
                        fullWidth
                      />
                    </Grid>
                  </>
                )}
                {/* Is Active Switch */}
                {/* <Grid item xs={12} style={{ marginTop: 14, marginBottom: 14 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={mediaGallery.isActive}
                        onChange={this.onChange}
                        name="isActive"
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </Grid> */}

                {/* Media Upload Section - Common for Image and Video, Hidden for Text */}
                {mediaGallery.mediaType !== "text" && (
                  <>
                    <Grid>
                      <Typography variant="body2" color="textSecondary" style={{ marginTop: 20, marginBottom: 20 }}>
                        Upload Image {mediaGallery.mediaType === "video" ? "(Poster/Thumbnail)" : ""}
                      </Typography>
                    </Grid>

                    <Grid container spacing={3} alignItems="center">
                      {/* Image Upload - Always shown for both types */}
                      <Grid item md={6}>
                        {!previewImage ? (
                          <ImageComp
                            src={getImageSource(`${mediaGallery.mediaUrl}`)}
                            defaultSrc={getImageSource()}
                            size={"100%"}
                          />
                        ) : (
                          <img
                            height="auto"
                            width="100%"
                            src={previewImage}
                            alt="preview_media"
                          />
                        )}
                      </Grid>
                      <Grid item md={6}>
                        <input
                          accept="image/png,image/jpeg"
                          style={{ display: "none" }}
                          id={"contained-button-file"}
                          type={"file"}
                          onChange={this.onUploadMedia}
                        />

                        <label htmlFor={"contained-button-file"}>
                          <Button
                            color="primary"
                            component="span"
                            disabled={isUploadingFile || !permissions.canEdit}
                            aria-label="Upload Media"
                            startIcon={<UploadIcon />}
                          >
                            UPLOAD IMAGE
                          </Button>
                        </label>
                      </Grid>
                    </Grid>
                  </>
                )}

                {/* Button - Save */}
                <Grid item xs={12} className={classes.buttonsContainer} style={{ marginTop: 20 }}>
                  <Link to={routes.mediaGalleryList.path}>
                    <Tooltip title="Cancel" arrow>
                      <Button
                        type={"button"}
                        aria-label={"Close"}
                        color={"error"}
                        startIcon={<CancelIcon />}
                      >
                        CANCEL
                      </Button>
                    </Tooltip>
                  </Link>
                  <Tooltip title="Save" arrow>
                    <Button
                      type={"submit"}
                      aria-label={"Save"}
                      startIcon={<SaveIcon />}
                      color={"primary"}
                      disabled={isLoadingSubmit || !permissions.canEdit}
                    >
                      SAVE
                    </Button>
                  </Tooltip>
                </Grid>
              </form>
            )}
          </SectionPaper>
        </Grid>
      </div>
    );
  }
}

// Component Properties
MediaGalleryCreateOrUpdate.propTypes = {
  list: PropTypes.func.isRequired,
  detail: PropTypes.func.isRequired,
  createOrUpdate: PropTypes.func.isRequired,
  upload: PropTypes.func.isRequired,
  messageShow: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

// Component State
function createOrEditState(state) {
  return {
    mediaGallery: state.mediaGallery,
  };
}

export default connect(createOrEditState, {
  list,
  detail,
  createOrUpdate,
  upload,
  messageShow,
})(withStyles(styles)(MediaGalleryCreateOrUpdate));